import { motion } from 'framer-motion';
import { Pencil, Trash2, CheckCircle2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ProductThumbnail } from './ProductThumbnail';
import { sourceMeta } from './sourceMeta';
import { listItemVariants } from './motion';
import { formatBdt, type Product } from '../mockKnowledge';

interface ProductRowProps {
  product: Product;
  onToggleInKnowledgeBase: (id: string) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
}

export function ProductRow({ product, onToggleInKnowledgeBase, onEdit, onDelete }: ProductRowProps) {
  const meta = sourceMeta[product.source] ?? sourceMeta.manual;
  const hasStock = typeof product.stock === 'number' && !Number.isNaN(product.stock);
  const outOfStock = hasStock && product.stock === 0;

  return (
    <motion.tr
      layout
      variants={listItemVariants}
      exit="exit"
      className="border-b border-slate-100 dark:border-slate-800/70 text-xs last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors"
    >
      <td className="py-2.5 pl-4 pr-3">
        <div className="flex items-center gap-2.5">
          <ProductThumbnail src={product.imageUrl} alt={product.title} />
          <div className="min-w-0">
            <p className="truncate font-bold text-slate-900 dark:text-slate-100">{product.title}</p>
            <p className="truncate text-[10px] text-slate-400 dark:text-slate-500">{product.category}</p>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap py-2.5 pr-3 font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatBdt(product.price)}</td>
      <td className={cn('whitespace-nowrap py-2.5 pr-3 font-medium', outOfStock ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400')}>
        {outOfStock ? 'Out of stock' : hasStock ? `${product.stock} in stock` : 'In stock'}
      </td>
      <td className="py-2.5 pr-3">
        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', meta.badgeClassName)}>
          {meta.name}
        </span>
      </td>
      <td className="py-2.5 pr-3">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={product.inKnowledgeBase}
            onChange={() => onToggleInKnowledgeBase(product.id)}
            aria-label={`Include ${product.title} in AI knowledge`}
          />
          <span className={cn('text-[11px] font-medium', product.inKnowledgeBase ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500')}>
            {product.inKnowledgeBase ? 'In AI' : 'Excluded'}
          </span>
        </label>
      </td>
      <td className="py-2.5 pr-4 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onEdit(product)}
            aria-label={`Edit ${product.title}`}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(product.id)}
            aria-label={`Delete ${product.title}`}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/50 hover:text-rose-600 dark:hover:text-rose-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </motion.tr>
  );
}

export default ProductRow;
